import { Injectable, Inject } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { BehaviorSubject, debounceTime, Observable, Subject } from "rxjs";
import { Store } from "@ngrx/store";

import { config, version } from "../../site-local";
import { ModelState } from "./state";
import {
    UpdateAction,
    DeleteAction,
    AddAction,
    ReplaceDatabaseAction,
    MergeAction,
} from "./actions";
import { assert } from "../../utils/debug";
import { ConsultationProcess } from "../../data/model";
import { NotificationService } from "../../services/notification.service";
import * as View from "../view/actions";

@Injectable()
export class UpdateCache {
    protected serviceUrl = config.ZBDSERVICEURL;

    private updates = {};
    private flushes$ = new Subject<void>();
    private reqOpts: {
        headers?: HttpHeaders;
        observe: "body";
        responseType: "json";
    };
    public isUpdating$ = new BehaviorSubject<boolean>(false);
    private _runningRequests = 0;
    private pathDates = {};

    constructor(
        protected http: HttpClient,
        private store: Store<ModelState>,
        private notify: NotificationService,
    ) {
        const headers = new HttpHeaders()
            .append("Content-Type", "application/json")
            .append("Accept", "application/json");
        this.reqOpts = {
            headers: headers,
            observe: "body",
            responseType: "json",
        };

        this.flushes$.pipe(debounceTime(500)).subscribe((_) => {
            this.flushUpdates();
        });
    }

    private wrapRequest<T>(
        observable: Observable<T>,
        onSuccess?,
        onError?,
        onFinalize?,
    ) {
        const onFinalizeMod = () => {
            this.finalizeRequest();
            if (onFinalize) {
                onFinalize();
            }
        };
        this.startRequest();
        return observable.subscribe(onSuccess, onError, onFinalizeMod);
    }

    private startRequest() {
        if (!this.isUpdating$.value && this._runningRequests == 0) {
            this.isUpdating$.next(true);
        }
        this._runningRequests++;
    }

    private finalizeRequest() {
        this._runningRequests--;
        if (this._runningRequests < 0) {
            this._runningRequests = 0;
        }
        if (this._runningRequests == 0) {
            this.isUpdating$.next(false);
        }
    }

    load(collection: string, id: number): void {
        const url = `${this.serviceUrl}/${collection}(${id})?$expand=*($levels=20)`;
        this.wrapRequest(
            this.http.get<ConsultationProcess>(url, this.reqOpts),
            (value) => {
                assert(
                    collection === "ConsultationProcesses",
                    "Cannot load entity from ${collection}.",
                );
                this.store.dispatch(new ReplaceDatabaseAction(value));
                this.store.dispatch(
                    View.Update.create("protocolLoadFailed", false),
                );
            },
            (error) => {
                this.notify.errorExt(
                    "Vorgang laden",
                    "Der Vorgang konnte nicht geladen werden.",
                    error,
                );
                this.store.dispatch(
                    View.Update.create("protocolLoadFailed", true),
                );
            },
        );
    }

    add(
        collection: string,
        entity: any,
        property: string,
        valueCollection: string,
        value: any,
    ): void {
        if (entity === undefined || entity === null) {
            assert(false, "Entity cannot be added:", entity);
            return;
        }

        const url = `${this.serviceUrl}/${collection}(${entity.Id})/${property}`;
        this.wrapRequest(
            this.http.post<any>(url, JSON.stringify(value), this.reqOpts),
            (newPartialValue) => {
                const url = `${this.serviceUrl}/${valueCollection}(${newPartialValue.Id})?$expand=*($levels=20)`;
                this.wrapRequest(
                    this.http.get(url, this.reqOpts),
                    (newValue) => {
                        const list: any[] = entity[property];
                        this.store.dispatch(new AddAction(list, newValue));
                    },
                );
            },
            (error) => {
                this.notify.errorExt(
                    "Änderungen durchführen",
                    "Die Änderung konnte nicht durchgeführt werden.",
                    error,
                );
            },
        );
    }

    addDetached(collection: string, entity: any): void {
        if (entity === undefined || entity === null) {
            assert(false, "Entity cannot be added:", entity);
            return;
        }

        const url = `${this.serviceUrl}/${collection}`;
        this.wrapRequest(
            this.http.post(url, JSON.stringify(entity), this.reqOpts),
            (newEntity) => {
                newEntity["@path"] = [collection];
                this.store.dispatch(new AddAction(entity, entity));
            },
            (error) => {
                this.notify.errorExt(
                    "Änderungen durchführen",
                    "Die Änderung konnte nicht durchgeführt werden.",
                    error,
                );
            },
        );
    }

    addSingle(
        collection: string,
        entity: any,
        property: string,
        valueCollection: string,
        value: any,
    ): void {
        if (
            entity === undefined ||
            entity === null ||
            value === undefined ||
            value === null
        ) {
            assert(false, "Entity cannot be added:", entity);
            return;
        }

        const url = `${this.serviceUrl}/${valueCollection}`;
        this.wrapRequest(
            this.http.post<any>(url, JSON.stringify(value), this.reqOpts),
            (newValue) => {
                const url = `${this.serviceUrl}/${collection}(${entity.Id})/${property}/$ref`;
                this.wrapRequest(
                    this.http.put(
                        url,
                        JSON.stringify({
                            "@odata.id": `${this.serviceUrl}/${valueCollection}(${newValue.Id})`,
                        }),
                        this.reqOpts,
                    ),
                    () => {
                        //newValue["@path"] = [...entity["@path"], property]
                        //this.update(collection, entity, property, newValue)
                    },
                );
            },
            (error) => {
                this.notify.errorExt(
                    "Änderungen durchführen",
                    "Die Änderung konnte nicht durchgeführt werden.",
                    error,
                );
            },
        );
    }

    remove(
        collection: string,
        entity: any,
        onSuccess?: () => void,
        onError?: (error: any) => boolean | void,
        onFinally?: () => void,
        handleDelete?: (entity: any) => void,
    ): void {
        if (
            entity === undefined ||
            entity === null ||
            !entity.Id ||
            !entity["@path"]
        ) {
            assert(false, "Entity cannot be deleted:", entity);
            return;
        }

        const path = entity["@path"];

        delete this.updates[path];
        this.wrapRequest(
            this.http.delete(
                `${this.serviceUrl}/${collection}(${entity["Id"]})`,
            ),
            (_) => {
                if (handleDelete) {
                    handleDelete(entity);
                } else {
                    this.store.dispatch(new DeleteAction(entity));
                }
                if (onSuccess) {
                    onSuccess();
                }
                if (onFinally) {
                    onFinally();
                }
            },
            (error) => {
                let showMessage = true;
                if (onError) {
                    showMessage = onError(error) !== false;
                }
                if (showMessage) {
                    this.notify.errorExt(
                        "Änderungen durchführen",
                        "Die Änderung konnte nicht durchgeführt werden.",
                        error,
                    );
                }
                if (onFinally) {
                    onFinally();
                }
            },
        );
    }

    update(
        collection: string,
        entity: any,
        property: string,
        value: any,
        flush: boolean,
    ): void {
        if (
            entity === undefined ||
            entity === null ||
            !entity.Id ||
            !entity["@path"]
        ) {
            assert(false, "Entity cannot be updated:", entity);
            return;
        }
        if (!this.isUpdating$.value) {
            this.isUpdating$.next(true);
        }

        const path = entity["@path"];
        const diff = {};
        diff["Id"] = entity.Id;
        diff[property] = value;

        if (path in this.updates) {
            this.updates[path].entity = Object.assign(
                this.updates[path].entity,
                diff,
            );
            this.updates[path].diff = Object.assign(
                this.updates[path].diff,
                diff,
            );
        } else {
            const newEntity = Object.assign({}, entity, diff);
            this.updates[path] = new Update(collection, newEntity, diff);
        }
        this.flushes$.next();
        if (flush) {
            // Display update before answer from server
            const tentative = Object.assign({}, entity, { [property]: value });
            this.store.dispatch(new UpdateAction(tentative));
        }
    }

    protected flushUpdates() {
        const timestamp = new Date().getTime();
        const updates = this.updates;
        this.updates = {};

        for (const path in updates) {
            this.pathDates[path] = timestamp;
            const update: Update = updates[path];
            const url = `${this.serviceUrl}/${update.collection}(${update.entity["Id"]})`;

            /*let remoteEntity = Object.assign({}, update.entity)
            delete remoteEntity["@path"]
            for (let key in remoteEntity) {
                if (remoteEntity[key] instanceof Object && remoteEntity[key].hasOwnProperty("@path")) {
                    delete remoteEntity[key]
                }
            }*/

            this.wrapRequest(
                this.http.put(url, JSON.stringify(update.diff), this.reqOpts),
                (_) => {
                    if (
                        !(path in this.updates) &&
                        this.pathDates[path] == timestamp
                    ) {
                        this.store.dispatch(
                            new MergeAction(
                                update.entity["@path"],
                                update.diff,
                            ),
                        );
                    }
                },
                (error) => {
                    this.notify.errorExt(
                        "Änderungen durchführen",
                        "Die Änderung konnte nicht durchgeführt werden.",
                        error,
                    );
                },
            );
        }
    }
}

class Update {
    constructor(
        public collection: string,
        public entity: any,
        public diff: any,
    ) {}
}
